import { ICONS } from './icons/iconData'
import type { IconName } from './icons/iconData'
import { cssVar } from '@/styles/tokens'
import type { ColorToken } from '@/styles/tokens'

const SIZE_TOKEN = { default: cssVar('sp-8'), s: cssVar('sp-6'), xs: cssVar('sp-5') } as const

/**
 * `svg.i` → `icon` (process/08-COMPONENT-CATALOGUE.md). **Zero occurrences of `data-cmp="icon"`
 * existed anywhere in this app until W156** — `tools/uispec/l1_conform.py` (Pass 12, W152) found
 * this as the build's single largest true gap, 73 flagged occurrences across almost every unit.
 *
 * **Size is the token map, not the prototype's own raw pixels** (16px/13px/11px): `--sp-8`/`--sp-6`/
 * `--sp-5` are 16px/12px/10px, a 1px delta on the two smaller sizes — the same tolerance W139's own
 * token mapping already established for type and spacing, recorded here rather than adding two new
 * pixel values to the closed vocabulary for one component's sake.
 *
 * **A colour is a token, never a literal** — the prototype's own usage (`style="color:var(--blue)"`
 * on a handful of dashboard/status icons) is exactly `ColorToken`'s own set.
 *
 * **`fill:none`/`stroke:currentColor` is the base rule for the `i-*` (outline) set** — the
 * prototype's own CSS (`svg.i{fill:none;stroke:currentColor}`).
 *
 * **`f-*` (filled) is a different base rule, `fill:currentColor;stroke:none`** (the prototype's own
 * `svg.fi{fill:currentColor;stroke:none}`, applied via a second class alongside `svg.i`) — **found
 * wrong here W175**: this component applied the outline rule to every icon regardless of prefix, on
 * the assumption (stated in this comment since W156, never verified past `f-shield`) that every
 * `f-*`/`b-*` path carries its own explicit `fill`. True for `b-*` (every brand logo's own path has
 * one); false for `f-home`/`f-shield`/`f-sliders`/`f-chart`/`f-rocket`/`f-gear`/`f-board`'s own
 * OUTER shape, which has none and was rendering outline-only everywhere it was used — one confirmed
 * instance was already worked around by hand-composing a one-off `<svg>` in `AuthCardBrand.tsx`
 * (W173) rather than fixing the root cause; the other six were found using this component for the
 * first time, wiring `Sidebar.tsx`'s group icons (W175). `b-*` is unaffected either way, since every
 * one of its own paths already carries an explicit `fill` that overrides whichever base rule is set.
 */
export function Icon({
  name,
  size = 'default',
  color,
  className,
}: {
  name: IconName
  size?: 'default' | 's' | 'xs'
  color?: ColorToken
  className?: string
}) {
  const icon = ICONS[name]
  const dimension = SIZE_TOKEN[size]
  const filled = name.startsWith('f-')
  return (
    <svg
      data-cmp="icon"
      viewBox={icon.viewBox}
      width={dimension}
      height={dimension}
      aria-hidden="true"
      focusable="false"
      style={{
        flex: 'none',
        fill: filled ? 'currentColor' : 'none',
        stroke: filled ? 'none' : 'currentColor',
        strokeWidth: 1.6,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        color: color ? cssVar(color) : undefined,
      }}
      className={className}
      // GENERATED, static, compile-time markup only (`src/components/icons/iconData.ts`) — never
      // user- or server-supplied content, so this is not an XSS surface.
      dangerouslySetInnerHTML={{ __html: icon.markup }}
    />
  )
}
